import type { EditorStore } from './editor-store';
import { validate } from './editor-validation';
import { showConfirm } from './editor-dialogs';
import { haptic } from './editor-haptic';
import type { History } from './editor-history';
import { copyObstacle } from './editor-clipboard';
import type { Schema, SchemaField } from './editor-schemas';
import {
  metaSchema, settingsSchema, spikeSchema, wallSchema, starSchema, fragmentSchema, powerupSchema, movingSchema,
  jumppadSchema, orbSchema,
  terrainFunctionSchema, terrainFlatSchema, terrainCeilingSchema, terrainTunnelSchema, segmentSchema,
  triggerBaseSchema, triggerParamSchemas, decorationSchema,
} from './editor-schemas';
import { LIMITS } from '@shared/limits';

const MAX_TAGS = LIMITS.MAX_TAGS;

/**
 * Render the tag-picker into `host`. Selected whitelist tags appear as cyan chips.
 * Non-whitelist (legacy) tags appear orange with remove-only affordance + warning strip.
 * `onChange` is called with the full new tag list whenever selection changes.
 */
export function renderTagPicker(
  host: HTMLElement,
  current: string[],
  allowed: readonly string[],
  onChange: (next: string[]) => void,
): void {
  const allowedSet = new Set(allowed);
  const selected = new Set(current);
  const selectedWhitelist = current.filter((t) => allowedSet.has(t));
  const legacy = current.filter((t) => !allowedSet.has(t));
  const atCap = selected.size >= MAX_TAGS;

  const chipStyle = (colorSet: 'selected' | 'unselected' | 'legacy') => {
    if (colorSet === 'selected') return 'background:#00ffff22;border:1px solid #00ffff;color:#00ffff';
    if (colorSet === 'legacy') return 'background:#ff880022;border:1px solid #ff880088;color:#ffaa44';
    return 'background:#111;border:1px solid #333;color:#aaa';
  };

  const selectedChipsHtml = [
    ...selectedWhitelist.map((t) =>
      `<span data-tag="${escapeHtml(t)}" data-state="selected" style="${chipStyle('selected')};padding:3px 8px;border-radius:10px;font-size:0.7rem;display:inline-flex;align-items:center;gap:4px;margin:2px">${escapeHtml(t)} <span data-remove style="cursor:pointer;color:#00ffff88">&#x2715;</span></span>`,
    ),
    ...legacy.map((t) =>
      `<span data-tag="${escapeHtml(t)}" data-legacy="true" style="${chipStyle('legacy')};padding:3px 8px;border-radius:10px;font-size:0.7rem;display:inline-flex;align-items:center;gap:4px;margin:2px">${escapeHtml(t)} <span data-remove style="cursor:pointer;color:#ffaa4488">&#x2715;</span></span>`,
    ),
  ].join(' ');

  const gridHtml = allowed.map((t) => {
    const isSelected = selected.has(t);
    const cls: 'selected' | 'unselected' = isSelected ? 'selected' : 'unselected';
    const disabled = !isSelected && atCap;
    return `<span data-tag-picker-choice="${escapeHtml(t)}" style="${chipStyle(cls)};padding:3px 8px;border-radius:10px;font-size:0.7rem;cursor:${disabled ? 'not-allowed' : 'pointer'};opacity:${disabled ? '0.45' : '1'};margin:2px">${escapeHtml(t)}${isSelected ? ' \u2713' : ''}</span>`;
  }).join(' ');

  const warning = legacy.length > 0
    ? `<div style="color:#ff8800;font-size:0.75rem;padding:6px 8px;background:#ff880011;border-radius:4px;margin-top:6px">\u26a0 ${legacy.map((t) => `&quot;${escapeHtml(t)}&quot;`).join(', ')} ${legacy.length > 1 ? "aren't on the list. They block publishing" : "isn't on the list. It blocks publishing"}. Remove to publish.</div>`
    : '';

  host.innerHTML = `
    <div style="background:#111;border:1px solid ${legacy.length > 0 ? '#ff880066' : '#333'};border-radius:4px;padding:8px;display:flex;flex-wrap:wrap;align-items:center;margin-bottom:8px;min-height:36px">
      ${selectedChipsHtml || '<span style="color:#666;font-size:0.7rem">No tags. Pick from below.</span>'}
    </div>
    ${warning}
    <div style="background:#111;border:1px solid #00ffff44;border-radius:4px;padding:12px;margin-top:6px">
      <div style="color:#888;font-size:0.7rem;margin-bottom:8px">PICK FROM (${selected.size}/${MAX_TAGS} selected):</div>
      <div style="display:flex;flex-wrap:wrap">${gridHtml}</div>
    </div>
  `;

  host.querySelectorAll<HTMLElement>('[data-tag-picker-choice]').forEach((el) => {
    el.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      const t = el.dataset.tagPickerChoice!;
      const next = new Set(selected);
      if (next.has(t)) {
        next.delete(t);
      } else {
        if (atCap) return;
        next.add(t);
      }
      onChange(Array.from(next));
    });
  });

  host.querySelectorAll<HTMLElement>('[data-tag] [data-remove]').forEach((el) => {
    el.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      const tag = (el.closest('[data-tag]') as HTMLElement).dataset.tag!;
      const next = current.filter((t) => t !== tag);
      onChange(next);
    });
  });
}
import {
  updateMeta, updateSettings, updateObstacleField, updateTerrain,
  removeObstacle, removeCollectible, removeSegment, reorderSegments,
  updateTrigger, removeTrigger, duplicateObstacle, duplicateSegment,
  addDecoration, removeDecoration, updateDecorationField,
} from './editor-commands';
import type { DecorationDef } from '@/game/level';

interface PendingTagPicker {
  pathStr: string;
  source: Record<string, unknown>;
  allowed: readonly string[];
  onChange: (path: readonly (string | number)[], value: unknown) => void;
}

export class EditorInspector {
  private host: HTMLElement;
  private store: EditorStore;
  private history: History;
  private _pendingTagPickers: PendingTagPicker[] = [];

  /** When true, a live input is dispatching its change — skip the re-render so
   *  the focused element (and any open native picker popup) isn't destroyed. */
  private _suppressRender = false;

  constructor(host: HTMLElement, store: EditorStore, history: History) {
    this.host = host;
    this.store = store;
    this.history = history;
    this.render();
    store.on('selection-changed', () => this.render());
    store.on('level-changed', () => this.render());
  }

  private render(): void {
    if (this._suppressRender) return;
    this._pendingTagPickers = [];
    const sel = this.store.selection;
    const lvl = this.store.level;
    let html = '<div class="group-label">INSPECTOR</div>';

    switch (sel.kind) {
      case 'none':
        html += `<div class="editor-empty">Select something from the outline or preview.</div>`;
        break;
      case 'meta':
        html += this.renderForm(metaSchema, lvl.meta as unknown as Record<string, unknown>, (path, value) =>
          this.history.execute(updateMeta(this.store, path, value)));
        break;
      case 'settings':
        html += this.renderForm(settingsSchema, lvl.settings as unknown as Record<string, unknown>, (path, value) =>
          this.history.execute(updateSettings(this.store, path, value)));
        break;
      case 'segment': {
        const seg = lvl.segments.find((x) => x.id === sel.segmentId);
        if (!seg) break;
        html += this.renderForm(segmentSchema, seg as unknown as Record<string, unknown>, (path, value) => {
          this.store.mutate((l) => {
            const s = l.segments.find((x) => x.id === sel.segmentId)!;
            writePath(s as unknown, path, value);
          });
        });
        const segIdx = lvl.segments.findIndex((x) => x.id === sel.segmentId);
        const canUp = segIdx > 0;
        const canDown = segIdx < lvl.segments.length - 1;
        const canDelete = lvl.segments.length > 1;
        html += `
  <div class="field-row" style="display:flex;gap:4px;flex-direction:row">
    <button data-act="seg-up" ${canUp ? '' : 'disabled'}>▲ Move Up</button>
    <button data-act="seg-down" ${canDown ? '' : 'disabled'}>▼ Move Down</button>
  </div>
  <div class="field-row">
    <button class="danger" data-act="seg-delete" ${canDelete ? '' : 'disabled'}>🗑 Delete Segment</button>
  </div>
  <div class="field-row" style="display:flex;gap:4px;flex-direction:row">
    <button data-act="seg-duplicate">📋 Duplicate</button>
    <button data-act="seg-duplicate-mirror">🪞 Mirror</button>
  </div>
  <div class="field-row"><button data-act="add-decoration">✨ Add Decoration</button></div>
`;
        break;
      }
      case 'terrain': {
        const seg = lvl.segments.find((x) => x.id === sel.segmentId);
        if (!seg) break;

        // Terrain type switcher (flat | function)
        html += `
    <div class="field-row">
      <label>Terrain Type</label>
      <select data-act="terrain-type">
        <option value="flat"${seg.terrain.type === 'flat' ? ' selected' : ''}>flat</option>
        <option value="function"${seg.terrain.type === 'function' ? ' selected' : ''}>function</option>
        <option value="tunnel"${seg.terrain.type === 'tunnel' ? ' selected' : ''}>tunnel</option>
      </select>
    </div>
  `;

        // Main terrain schema
        const schema =
          seg.terrain.type === 'flat' ? terrainFlatSchema :
          seg.terrain.type === 'tunnel' ? terrainTunnelSchema :
          terrainFunctionSchema;
        html += this.renderForm(schema, seg.terrain as unknown as Record<string, unknown>, (path, value) => {
          const copy = JSON.parse(JSON.stringify(seg.terrain));
          writePath(copy, path, value);
          this.history.execute(updateTerrain(this.store, sel.segmentId, copy));
        });

        // Ceiling toggle
        const hasCeiling = seg.terrain.ceiling !== null;
        if (!hasCeiling && seg.terrain.type !== 'tunnel') {
          html += `<div class="field-row"><button data-act="add-ceiling">➕ Add ceiling</button></div>`;
        } else if (hasCeiling) {
          html += this.renderForm(terrainCeilingSchema, seg.terrain as unknown as Record<string, unknown>, (path, value) => {
            const copy = JSON.parse(JSON.stringify(seg.terrain));
            writePath(copy, path, value);
            this.history.execute(updateTerrain(this.store, sel.segmentId, copy));
          });
          html += `<div class="field-row"><button class="danger" data-act="remove-ceiling">🗑 Remove ceiling</button></div>`;
        }
        break;
      }
      case 'obstacle': {
        const seg = lvl.segments.find((x) => x.id === sel.segmentId);
        if (!seg) break;
        const o = seg.obstacles[sel.index];
        if (!o) break;
        let schema: Schema;
        switch (o.type) {
          case 'spike': schema = spikeSchema; break;
          case 'wall': schema = wallSchema; break;
          case 'moving': schema = movingSchema; break;
          case 'jumppad': schema = jumppadSchema; break;
          case 'orb': schema = orbSchema; break;
          default: schema = wallSchema;
        }
        html += this.renderForm(schema, o as unknown as Record<string, unknown>, (path, value) =>
          this.history.execute(updateObstacleField(this.store, sel.segmentId, sel.index, path, value)));
        html += `<div class="field-row"><button class="danger" data-act="delete">🗑 Delete</button></div>`;
        html += `<div class="field-row"><button data-act="duplicate">📋 Duplicate</button></div>`;
        html += `<div class="field-row"><button data-act="copy">📎 Copy</button></div>`;
        html += this.nudgeButtonsHtml();
        break;
      }
      case 'collectible': {
        const c = lvl.collectibles[sel.index];
        if (!c) break;
        let schema: Schema;
        switch (c.type) {
          case 'star': schema = starSchema; break;
          case 'fragment': schema = fragmentSchema; break;
          case 'powerup': schema = powerupSchema; break;
          default: schema = starSchema;
        }
        html += this.renderForm(schema, c as unknown as Record<string, unknown>, (path, value) => {
          this.store.mutate((l) => { writePath(l.collectibles[sel.index] as unknown, path, value); });
        });
        html += `<div class="field-row"><button class="danger" data-act="delete-collectible">🗑 Delete</button></div>`;
        html += this.nudgeButtonsHtml();
        break;
      }
      case 'decoration': {
        const seg = lvl.segments.find((x) => x.id === sel.segmentId);
        if (!seg) break;
        const d = seg.decorations[sel.index];
        if (!d) break;
        html += this.renderForm(decorationSchema, {
          ...d,
          properties: JSON.stringify(d.properties, null, 2),
        } as unknown as Record<string, unknown>, (path, value) => {
          let actualValue: unknown = value;
          if (path.join('.') === 'properties' && typeof value === 'string') {
            try { actualValue = JSON.parse(value); }
            catch { return; }
          }
          this.history.execute(updateDecorationField(this.store, sel.segmentId, sel.index, path, actualValue));
        });
        html += `<div class="field-row"><button class="danger" data-act="delete-decoration">🗑 Delete Decoration</button></div>`;
        break;
      }
      case 'trigger': {
        const t = lvl.triggers.find((x) => x.id === sel.id);
        if (!t) break;
        html += this.renderForm(triggerBaseSchema, t as unknown as Record<string, unknown>, (path, value) =>
          this.history.execute(updateTrigger(this.store, sel.id, path, value)));
        const paramSchema = triggerParamSchemas[t.type];
        if (paramSchema) {
          html += this.renderForm(paramSchema, t as unknown as Record<string, unknown>, (path, value) =>
            this.history.execute(updateTrigger(this.store, sel.id, path, value)));
        }
        html += `<div class="field-row"><button class="danger" data-act="delete-trigger">🗑 Delete Trigger</button></div>`;
        break;
      }
    }

    // Collect issues that touch the current selection
    const val = validate(lvl);
    const selKey = JSON.stringify(sel);
    const selIssues = [...val.blocking, ...val.warnings].filter((i) => JSON.stringify(i.jumpTo) === selKey);
    if (selIssues.length > 0) {
      html += '<div class="issues"><h4>Issues</h4><ul>';
      for (const i of selIssues) html += `<li>${escapeHtml(i.message)}</li>`;
      html += '</ul></div>';
    }

    // Preserve focus across re-renders driven by non-input sources (e.g.
    // selection change, external mutation). Re-renders from live-input edits
    // are suppressed upstream, so caret/selection state survives naturally.
    const active = document.activeElement as HTMLElement | null;
    const activeId = active && this.host.contains(active) ? active.id : null;

    this.host.innerHTML = html;

    // Post-process tag-picker mounts
    this.host.querySelectorAll<HTMLElement>('.tag-picker-mount').forEach((el) => {
      const pathStr = el.dataset.path!;
      const pending = this._pendingTagPickers.find((p) => p.pathStr === pathStr);
      if (!pending) return;
      const pathArr = pathStr.split('.') as (string | number)[];
      const currentTags = (readPath(pending.source, pathArr) as string[] | undefined) ?? [];
      renderTagPicker(el, currentTags, pending.allowed, (next) => {
        pending.onChange(pathArr, next);
      });
    });

    if (activeId) {
      this.host.querySelector<HTMLElement>(`#${CSS.escape(activeId)}`)?.focus();
    }

    // Delete handlers
    this.host.querySelector('[data-act="delete"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (sel.kind !== 'obstacle') return;
      if (!(await showConfirm('Delete obstacle', 'Remove this obstacle?'))) return;
      haptic('delete');
      this.history.execute(removeObstacle(this.store, sel.segmentId, sel.index));
      this.store.setSelection({ kind: 'none' });
    });
    this.host.querySelector('[data-act="duplicate"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (sel.kind !== 'obstacle') return;
      this.history.execute(duplicateObstacle(this.store, sel.segmentId, sel.index));
    });
    this.host.querySelector('[data-act="copy"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (sel.kind !== 'obstacle') return;
      const o = lvl.segments.find((s) => s.id === sel.segmentId)?.obstacles[sel.index];
      if (!o) return;
      copyObstacle(o);
    });

    // Nudge handlers
    const nudgeObstacle = (dx: number, dy: number) => {
      if (sel.kind !== 'obstacle') return;
      const o = lvl.segments.find((s) => s.id === sel.segmentId)?.obstacles[sel.index];
      if (!o) return;
      if (dx !== 0) this.history.execute(updateObstacleField(this.store, sel.segmentId, sel.index, ['position', 'along'], o.position.along + dx));
      if (dy !== 0) this.history.execute(updateObstacleField(this.store, sel.segmentId, sel.index, ['position', 'up'], o.position.up + dy));
    };
    const nudgeCollectible = (dx: number, dy: number) => {
      if (sel.kind !== 'collectible') return;
      const c = lvl.collectibles[sel.index];
      if (!c) return;
      this.store.mutate((l) => {
        const cc = l.collectibles[sel.index];
        cc.position.along += dx;
        cc.position.up += dy;
      });
    };
    const nudge = (dx: number, dy: number) => {
      if (sel.kind === 'obstacle') nudgeObstacle(dx, dy);
      else if (sel.kind === 'collectible') nudgeCollectible(dx, dy);
    };

    this.host.querySelector('[data-act="nudge-up"]')?.addEventListener('pointerdown', (e) => { e.stopPropagation(); nudge(0, 1); });
    this.host.querySelector('[data-act="nudge-down"]')?.addEventListener('pointerdown', (e) => { e.stopPropagation(); nudge(0, -1); });
    this.host.querySelector('[data-act="nudge-left"]')?.addEventListener('pointerdown', (e) => { e.stopPropagation(); nudge(-1, 0); });
    this.host.querySelector('[data-act="nudge-right"]')?.addEventListener('pointerdown', (e) => { e.stopPropagation(); nudge(1, 0); });
    this.host.querySelector('[data-act="delete-collectible"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (sel.kind !== 'collectible') return;
      if (!(await showConfirm('Delete collectible', 'Remove this collectible?'))) return;
      haptic('delete');
      this.history.execute(removeCollectible(this.store, sel.index));
      this.store.setSelection({ kind: 'none' });
    });
    this.host.querySelector('[data-act="delete-trigger"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (sel.kind !== 'trigger') return;
      if (!(await showConfirm('Delete trigger', 'Remove this trigger?'))) return;
      this.history.execute(removeTrigger(this.store, sel.id));
      this.store.setSelection({ kind: 'none' });
    });

    this.host.querySelector('[data-act="delete-decoration"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (sel.kind !== 'decoration') return;
      if (!(await showConfirm('Delete decoration', 'Remove this decoration?'))) return;
      this.history.execute(removeDecoration(this.store, sel.segmentId, sel.index));
      this.store.setSelection({ kind: 'none' });
    });

    this.host.querySelector('[data-act="add-decoration"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      const def: DecorationDef = {
        type: 'particle_emitter',
        position: { along: 0, across: 0, up: 0 },
        properties: {},
      };
      this.history.execute(addDecoration(this.store, curSegId, def));
    });

    // Terrain type switcher
    this.host.querySelector<HTMLSelectElement>('[data-act="terrain-type"]')?.addEventListener('change', (e) => {
      if (sel.kind !== 'terrain') return;
      const newType = (e.target as HTMLSelectElement).value as 'flat' | 'function' | 'tunnel';
      const segLocal = lvl.segments.find((x) => x.id === sel.segmentId);
      if (!segLocal) return;
      const copy = JSON.parse(JSON.stringify(segLocal.terrain));
      copy.type = newType;
      if (newType === 'function' && copy.floor && copy.floor.function === 'constant') {
        copy.floor = { function: 'sin', amplitude: 30, frequency: 0.01, offset: copy.floor.offset, phase: 0 };
      } else if (newType === 'flat') {
        copy.floor = { function: 'constant', offset: copy.floor?.offset ?? 100 };
      } else if (newType === 'tunnel') {
        copy.tunnel = copy.tunnel ?? { shape: 'circle', radius: 80, parameters: { shapeMorph: 0, rosePetals: 3 }, morphTargets: [] };
        copy.floor = null;
        copy.ceiling = null;
      }
      this.history.execute(updateTerrain(this.store, sel.segmentId, copy));
    });

    // Add ceiling
    this.host.querySelector('[data-act="add-ceiling"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (sel.kind !== 'terrain') return;
      const segLocal = lvl.segments.find((x) => x.id === sel.segmentId);
      if (!segLocal) return;
      const copy = JSON.parse(JSON.stringify(segLocal.terrain));
      copy.ceiling = { function: 'constant', offset: 200 };
      this.history.execute(updateTerrain(this.store, sel.segmentId, copy));
    });

    // Remove ceiling
    this.host.querySelector('[data-act="remove-ceiling"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (sel.kind !== 'terrain') return;
      if (!(await showConfirm('Remove ceiling', 'Remove the ceiling from this segment?'))) return;
      const segLocal = lvl.segments.find((x) => x.id === sel.segmentId);
      if (!segLocal) return;
      const copy = JSON.parse(JSON.stringify(segLocal.terrain));
      copy.ceiling = null;
      this.history.execute(updateTerrain(this.store, sel.segmentId, copy));
    });

    // Segment duplicate / mirror
    const curSegId = sel.kind === 'segment' ? sel.segmentId : null;
    this.host.querySelector('[data-act="seg-duplicate"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      this.history.execute(duplicateSegment(this.store, curSegId, false));
    });
    this.host.querySelector('[data-act="seg-duplicate-mirror"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      this.history.execute(duplicateSegment(this.store, curSegId, true));
    });

    // Segment reorder up
    this.host.querySelector('[data-act="seg-up"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      const ids = lvl.segments.map((x) => x.id);
      const i = ids.indexOf(curSegId);
      if (i <= 0) return;
      [ids[i - 1], ids[i]] = [ids[i], ids[i - 1]];
      this.history.execute(reorderSegments(this.store, ids));
    });

    // Segment reorder down
    this.host.querySelector('[data-act="seg-down"]')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      const ids = lvl.segments.map((x) => x.id);
      const i = ids.indexOf(curSegId);
      if (i < 0 || i >= ids.length - 1) return;
      [ids[i], ids[i + 1]] = [ids[i + 1], ids[i]];
      this.history.execute(reorderSegments(this.store, ids));
    });

    // Segment delete
    this.host.querySelector('[data-act="seg-delete"]')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (!curSegId) return;
      if (lvl.segments.length <= 1) return;
      if (!(await showConfirm('Delete segment', 'Remove this segment and all its obstacles?'))) return;
      this.history.execute(removeSegment(this.store, curSegId));
      this.store.setSelection({ kind: 'none' });
    });
  }

  private nudgeButtonsHtml(): string {
    return `
    <div class="nudge-grid">
      <div></div>
      <button data-act="nudge-up">▲</button>
      <div></div>
      <button data-act="nudge-left">◀</button>
      <div></div>
      <button data-act="nudge-right">▶</button>
      <div></div>
      <button data-act="nudge-down">▼</button>
      <div></div>
    </div>
  `;
  }

  private renderForm(
    schema: Schema,
    source: Record<string, unknown>,
    onChange: (path: readonly (string | number)[], value: unknown) => void,
  ): string {
    const rows: string[] = [`<h3 class="inspector-title">${escapeHtml(schema.title)}</h3>`];
    for (const f of schema.fields) {
      rows.push(this.renderField(f, source, onChange));
    }
    return rows.join('');
  }

  private renderField(
    f: SchemaField, source: Record<string, unknown>,
    onChange: (path: readonly (string | number)[], value: unknown) => void,
  ): string {
    const currentRaw = readPath(source, f.path);
    const id = `f_${f.path.join('_')}`;
    let input = '';
    switch (f.field.type) {
      case 'number':
        input = `<input id="${id}" type="number" value="${currentRaw ?? 0}" min="${f.field.min ?? ''}" max="${f.field.max ?? ''}" step="${f.field.step ?? 1}"/>`;
        break;
      case 'string':
        if (f.field.multiline) {
          input = `<textarea id="${id}" rows="3">${escapeHtml(String(currentRaw ?? ''))}</textarea>`;
        } else {
          input = `<input id="${id}" type="text" value="${escapeHtml(String(currentRaw ?? ''))}"/>`;
        }
        break;
      case 'color':
        input = `<input id="${id}" type="color" value="${String(currentRaw ?? '#000000')}"/>`;
        break;
      case 'enum': {
        const labels = f.field.labels;
        const opts = f.field.options.map((o, i) => {
          const text = labels?.[i] ?? o;
          return `<option value="${o}"${o === currentRaw ? ' selected' : ''}>${escapeHtml(text)}</option>`;
        }).join('');
        input = `<select id="${id}">${opts}</select>`;
        break;
      }
      case 'boolean':
        input = `<input id="${id}" type="checkbox" ${currentRaw ? 'checked' : ''}/>`;
        break;
      case 'tags':
        input = `<input id="${id}" type="text" value="${escapeHtml(((currentRaw as string[]) ?? []).join(', '))}"/>`;
        break;
      case 'tag-picker':
        // Render a placeholder; after outer innerHTML is set, renderTagPicker fills it.
        this._pendingTagPickers.push({
          pathStr: f.path.join('.'),
          source,
          allowed: f.field.allowed,
          onChange,
        });
        return `<div class="field-row"><label>${escapeHtml(f.label)}</label><div class="tag-picker-mount" data-path="${f.path.join('.')}"></div></div>`;
    }

    // Wire change on the next tick once the HTML is in the DOM.
    queueMicrotask(() => {
      const el = this.host.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`#${id}`);
      if (!el) return;
      // Suppress the re-render triggered by the resulting mutation so the
      // live input (and any open native color picker) stays in the DOM.
      // The input already shows the new value; the data model catches up
      // silently. The next natural re-render (selection change, blur-driven
      // change event on a different field, external mutation) will reflect
      // any dependent-field updates.
      const dispatch = (v: unknown) => {
        this._suppressRender = true;
        try { onChange(f.path, v); } finally { this._suppressRender = false; }
      };
      el.addEventListener('change', () => dispatch(this.readInputValue(el, f.field)));
      el.addEventListener('input', () => dispatch(this.readInputValue(el, f.field)));
    });

    return `<div class="field-row"><label for="${id}">${escapeHtml(f.label)}</label>${input}</div>`;
  }

  private readInputValue(
    el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    field: SchemaField['field'],
  ): unknown {
    switch (field.type) {
      case 'number': return parseFloat((el as HTMLInputElement).value);
      case 'boolean': return (el as HTMLInputElement).checked;
      case 'tags': return (el as HTMLInputElement).value.split(',').map((s) => s.trim()).filter(Boolean);
      default: return (el as HTMLInputElement).value;
    }
  }
}

function readPath(obj: unknown, path: readonly (string | number)[]): unknown {
  let cur: unknown = obj;
  for (const seg of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[String(seg)];
  }
  return cur;
}

function writePath(obj: unknown, path: readonly (string | number)[], value: unknown): void {
  let cur = obj as Record<string, unknown>;
  for (let i = 0; i < path.length - 1; i++) {
    if (cur[String(path[i])] == null) cur[String(path[i])] = {};
    cur = cur[String(path[i])] as Record<string, unknown>;
  }
  cur[String(path[path.length - 1])] = value;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}
